/**
 * Iter 232 · AirQ account portal (pad_operator + aeromed_admin).
 * Standalone auth flow — separate from admin JWT + partner JWT + staff JWT.
 * Token stored in localStorage under "airq_account_token".
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { AirplaneTilt, SignOut, House, User, Gear, Power, FirstAidKit, UsersThree, ChartLineUp, ArrowClockwise, Trash, Plus } from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "airq_account_token";

const airqApi = axios.create({ baseURL: API });
airqApi.interceptors.request.use(cfg => {
  const t = localStorage.getItem(TOKEN_KEY);
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});


// =====================================================
// Login
// =====================================================
export function AirqAccountLogin() {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [otpStage, setOtpStage] = useState(null);   // {challenge_id, ttl_seconds, email_masked}
  const [otp, setOtp] = useState("");
  useEffect(() => {
    if (localStorage.getItem(TOKEN_KEY)) nav("/airq/dashboard", { replace: true });
  }, [nav]);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await axios.post(`${API}/airq-auth/login`,
                                    { email, password });
      if (r.data.requires_otp) {
        setOtpStage(r.data);
        toast.success(`OTP sent to ${r.data.email_masked}`);
      } else {
        localStorage.setItem(TOKEN_KEY, r.data.access_token);
        toast.success(`Welcome ${r.data.account.name}`);
        nav("/airq/dashboard", { replace: true });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    }
    setLoading(false);
  };
  const verify = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await axios.post(`${API}/airq-auth/login/verify-otp`,
        { challenge_id: otpStage.challenge_id, otp });
      localStorage.setItem(TOKEN_KEY, r.data.access_token);
      toast.success(`Welcome ${r.data.account.name}`);
      nav("/airq/dashboard", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Wrong OTP");
    }
    setLoading(false);
  };
  const resend = async () => {
    try {
      const r = await axios.post(`${API}/airq-auth/login/resend-otp`,
        { challenge_id: otpStage.challenge_id });
      setOtpStage(r.data);
      setOtp("");
      toast.success("New code sent");
    } catch (err) { toast.error(err?.response?.data?.detail || "Resend failed"); }
  };
  return (
    <div className="min-h-screen bg-[#F5F3EE] flex items-center justify-center p-4">
      <form onSubmit={otpStage ? verify : submit} data-testid="airq-login-form"
              className="bg-white rounded-lg border shadow-sm p-6 w-full max-w-md">
        <div className="flex items-center gap-2 mb-6">
          <AirplaneTilt size={28} className="text-[#0F766E]"/>
          <div>
            <div className="font-heading text-xl">AirQ portal</div>
            <div className="text-xs text-[#5C5A55]">
              Pad operator · Aeromed admin · Regulator
            </div>
          </div>
        </div>
        {!otpStage ? (
          <>
            <label className="block text-[11px] uppercase tracking-wider text-[#8A8780] mb-1">Email</label>
            <input type="email" required value={email} onChange={e => setEmail(e.target.value)}
                     data-testid="airq-login-email"
                     className="w-full border rounded px-3 py-2 mb-3 text-sm"/>
            <label className="block text-[11px] uppercase tracking-wider text-[#8A8780] mb-1">Password</label>
            <input type="password" required value={password} onChange={e => setPassword(e.target.value)}
                     data-testid="airq-login-password"
                     className="w-full border rounded px-3 py-2 mb-4 text-sm"/>
            <button type="submit" disabled={loading} data-testid="airq-login-submit"
                      className="w-full py-2 rounded bg-[#0F766E] text-white disabled:opacity-50">
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </>
        ) : (
          <>
            <div className="p-3 bg-[#F0F9FF] border border-[#7DD3FC] rounded text-xs mb-4 text-[#0C4A6E]">
              We sent a 6-digit code to <b>{otpStage.email_masked}</b>. Enter it to complete sign-in.
            </div>
            <label className="block text-[11px] uppercase tracking-wider text-[#8A8780] mb-1">OTP</label>
            <input required value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g,"").slice(0,6))}
                     inputMode="numeric" maxLength={6}
                     data-testid="airq-login-otp"
                     className="w-full border rounded px-3 py-2 mb-4 text-lg tracking-[.5em] text-center font-mono"/>
            <button type="submit" disabled={loading || otp.length !== 6}
                      data-testid="airq-login-verify"
                      className="w-full py-2 rounded bg-[#0F766E] text-white disabled:opacity-50">
              {loading ? "Verifying…" : "Verify & sign in"}
            </button>
            <div className="flex justify-between mt-3 text-xs">
              <button type="button" onClick={resend} data-testid="airq-login-resend"
                        className="text-[#0369A1]">Resend code</button>
              <button type="button" onClick={() => { setOtpStage(null); setOtp(""); }}
                        className="text-[#8A8780]">Cancel</button>
            </div>
          </>
        )}
        <p className="text-[11px] text-[#8A8780] mt-4 text-center">
          Ask your Rescigo admin for account credentials.
        </p>
      </form>
    </div>
  );
}


// =====================================================
// Dashboard
// =====================================================
export function AirqAccountDashboard() {
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [summary, setSummary] = useState(null);

  const loadSummary = () => airqApi.get("/airq-auth/summary")
    .then(r => setSummary(r.data));

  useEffect(() => {
    if (!localStorage.getItem(TOKEN_KEY)) {
      nav("/airq/login", { replace: true }); return;
    }
    Promise.all([
      airqApi.get("/airq-auth/me").then(r => setMe(r.data)),
      loadSummary(),
    ]).catch(err => {
      if (err?.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        nav("/airq/login", { replace: true });
      } else {
        toast.error(err?.response?.data?.detail || "Failed to load");
      }
    });
  }, [nav]);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    nav("/airq/login", { replace: true });
  };

  if (!me) return <div className="p-8 text-sm text-[#8A8780]">Loading…</div>;
  return (
    <div className="min-h-screen bg-[#F5F3EE]" data-testid="airq-dashboard-root">
      <header className="bg-white border-b p-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <AirplaneTilt size={22} className="text-[#0F766E]"/>
          <div>
            <div className="font-heading text-lg">AirQ portal</div>
            <div className="text-[11px] text-[#5C5A55]">
              {me.name} · {me.subtype} · {me.email}
            </div>
          </div>
        </div>
        <button onClick={logout} data-testid="airq-logout"
                  className="flex items-center gap-1 text-sm text-[#5C5A55] hover:text-[#B91C1C]">
          <SignOut size={14}/> Sign out
        </button>
      </header>
      <main className="p-4 max-w-6xl mx-auto space-y-4">
        {!summary ? <div className="text-sm text-[#8A8780]">Loading…</div>
          : summary.subtype === "pad_operator" ? <PadOperator s={summary} refreshSummary={loadSummary}/>
          : summary.subtype === "aeromed_admin" ? <AeromedAdmin s={summary}/>
          : (summary.subtype === "dgca" || summary.subtype === "state_aviation")
              ? <RegulatorPanel s={summary}/>
          : <div className="text-sm">Unknown subtype: {summary.subtype}</div>}
      </main>
    </div>
  );
}

// =====================================================
// Pad Operator — sidebar-navigated portal (Iter 236)
// =====================================================
const PAD_MENU = [
  { key: "overview",   label: "Overview",           icon: House      },
  { key: "profile",    label: "Profile",            icon: User       },
  { key: "settings",   label: "Settings",           icon: Gear       },
  { key: "status",     label: "Pad on/off",         icon: Power      },
  { key: "dispatches", label: "Incoming dispatches", icon: FirstAidKit },
  { key: "staff",      label: "Managers & staff",   icon: UsersThree },
  { key: "revenue",    label: "Revenue",            icon: ChartLineUp },
];

function PadOperator({ s, refreshSummary }) {
  const [tab, setTab] = useState("overview");
  const pad = s.pad || {};
  return (
    <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-4"
           data-testid="pad-operator-panel">
      {/* Sidebar */}
      <aside className="bg-white rounded-lg border p-2 h-max md:sticky md:top-4">
        <div className="px-2 py-3 border-b mb-2">
          <div className="text-[10px] uppercase tracking-wider text-[#8A8780]">Pad operator</div>
          <div className="font-heading text-sm mt-0.5 truncate" title={pad.name}>
            {pad.name || "Not assigned"}
          </div>
          <div className="text-[10px] text-[#8A8780] mt-0.5 font-mono">
            {pad.helipad_id || "—"}
          </div>
        </div>
        {PAD_MENU.map(m => {
          const Icon = m.icon;
          const active = tab === m.key;
          return (
            <button key={m.key}
                      data-testid={`pad-menu-${m.key}`}
                      onClick={() => setTab(m.key)}
                      className={`w-full flex items-center gap-2 px-3 py-2 text-sm rounded transition ${
                        active
                          ? "bg-[#0F766E]/10 text-[#0F766E] font-medium"
                          : "text-[#5C5A55] hover:bg-[#F5F3EE]"}`}>
              <Icon size={16}/>{m.label}
            </button>
          );
        })}
      </aside>
      {/* Panel */}
      <section className="min-w-0">
        {tab === "overview"   && <PadOverview s={s}/>}
        {tab === "profile"    && <PadProfile refreshSummary={refreshSummary}/>}
        {tab === "settings"   && <PadSettings/>}
        {tab === "status"     && <PadStatus refreshSummary={refreshSummary}/>}
        {tab === "dispatches" && <PadDispatches/>}
        {tab === "staff"      && <PadStaff/>}
        {tab === "revenue"    && <PadRevenue/>}
      </section>
    </div>
  );
}

function PadOverview({ s }) {
  const p = s.pad || {};
  return (
    <div className="space-y-4">
      <div className="bg-white rounded-lg border p-5">
        <div className="text-xs uppercase tracking-wider text-[#8A8780]">Your helipad</div>
        <div className="font-heading text-2xl mt-1">{p.name || "Not assigned"}</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4 text-sm">
          <Kv k="Helipad ID" v={p.helipad_id}/>
          <Kv k="Govt ID"    v={p.govt_id}/>
          <Kv k="Kind"       v={p.kind}/>
          <Kv k="Ownership"  v={p.ownership}/>
          <Kv k="District"   v={p.district}/>
          <Kv k="LSGD"       v={p.lsgd}/>
          <Kv k="Ward"       v={p.ward}/>
          <Kv k="Status"     v={p.operational_status || "on"}/>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="bg-white rounded-lg border p-4">
          <div className="text-xs uppercase tracking-wider text-[#8A8780] mb-2">
            Active missions · {(s.active_missions || []).length}
          </div>
          {(s.active_missions || []).length === 0
            ? <div className="text-sm text-[#8A8780]">No active flights touching your pad</div>
            : s.active_missions.map(m => (
              <div key={m.id} className="py-1.5 border-b last:border-0 text-sm">
                <div className="font-medium">{m.mission_no}</div>
                <div className="text-[11px] text-[#8A8780]">
                  {m.aircraft_call_sign} · {m.mission_kind}
                </div>
              </div>
            ))}
        </div>
        <div className="bg-white rounded-lg border p-4">
          <div className="text-xs uppercase tracking-wider text-[#8A8780] mb-2">
            Approvals for this pad
          </div>
          {(s.approvals || []).length === 0
            ? <div className="text-sm text-[#8A8780]">No approvals in flight</div>
            : s.approvals.map(a => (
              <div key={a.id} className="py-1.5 border-b last:border-0 text-sm">
                <div className="font-medium">{a.approval_no}</div>
                <div className="text-[11px] text-[#8A8780]">{a.status}</div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}

function PadProfile({ refreshSummary }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const load = () => airqApi.get("/airq-auth/pad/profile")
    .then(r => { setData(r.data);
      setForm({ name: r.data.account.name || "",
                 phone: r.data.account.phone || "",
                 contact_phone: r.data.pad.contact_phone || "",
                 address: r.data.pad.address || "" }); });
  useEffect(() => { load(); }, []);
  const save = async () => {
    setSaving(true);
    try {
      await airqApi.patch("/airq-auth/pad/profile", form);
      toast.success("Profile saved");
      await load();
      refreshSummary?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    setSaving(false);
  };
  if (!data) return <div className="text-sm text-[#8A8780]">Loading…</div>;
  return (
    <div className="bg-white rounded-lg border p-5 space-y-4" data-testid="pad-profile">
      <div>
        <div className="font-heading text-lg">Profile</div>
        <div className="text-xs text-[#8A8780]">Your operator + pad contact details.</div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="Account holder"
                 value={form.name}
                 onChange={v => setForm({ ...form, name: v })}
                 testid="pf-name"/>
        <Field label="Personal phone"
                 value={form.phone}
                 onChange={v => setForm({ ...form, phone: v })}
                 testid="pf-phone"/>
        <Field label="Pad contact phone"
                 value={form.contact_phone}
                 onChange={v => setForm({ ...form, contact_phone: v })}
                 testid="pf-contact"/>
        <Field label="Address"
                 value={form.address}
                 onChange={v => setForm({ ...form, address: v })}
                 testid="pf-address"/>
      </div>
      <div className="flex justify-end">
        <button onClick={save} disabled={saving} data-testid="pf-save"
                  className="px-4 py-2 rounded bg-[#0F766E] text-white text-sm disabled:opacity-50">
          {saving ? "Saving…" : "Save profile"}
        </button>
      </div>
    </div>
  );
}

function PadSettings() {
  const [prefs, setPrefs] = useState(null);
  const [pwd, setPwd] = useState({ current_password: "", new_password: "", confirm: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => { airqApi.get("/airq-auth/pad/settings")
    .then(r => setPrefs(r.data.prefs || {})); }, []);
  const togglePref = (k) => {
    const next = { ...prefs, [k]: !prefs[k] };
    setPrefs(next);
    airqApi.patch("/airq-auth/pad/settings", { [k]: next[k] })
      .then(() => toast.success("Preference saved"))
      .catch(() => toast.error("Failed to save"));
  };
  const changePw = async () => {
    if (!pwd.new_password || pwd.new_password.length < 6)
      return toast.error("Password must be at least 6 characters");
    if (pwd.new_password !== pwd.confirm)
      return toast.error("Passwords don't match");
    setSaving(true);
    try {
      await airqApi.patch("/airq-auth/pad/settings",
        { current_password: pwd.current_password, new_password: pwd.new_password });
      toast.success("Password updated");
      setPwd({ current_password: "", new_password: "", confirm: "" });
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    setSaving(false);
  };
  if (!prefs) return <div className="text-sm text-[#8A8780]">Loading…</div>;
  return (
    <div className="space-y-4" data-testid="pad-settings">
      <div className="bg-white rounded-lg border p-5">
        <div className="font-heading text-lg">Notifications</div>
        <div className="text-xs text-[#8A8780] mb-3">Choose how we reach you.</div>
        {[
          ["notify_dispatch_email", "Email on new dispatch"],
          ["notify_dispatch_sms",   "SMS on new dispatch"],
          ["notify_maintenance_email", "Email on maintenance requests"],
        ].map(([k, label]) => (
          <label key={k} className="flex items-center justify-between py-2 border-b last:border-0 text-sm">
            <span>{label}</span>
            <input type="checkbox" checked={!!prefs[k]}
                     onChange={() => togglePref(k)}
                     data-testid={`pref-${k}`}
                     className="w-4 h-4 accent-[#0F766E]"/>
          </label>
        ))}
      </div>
      <div className="bg-white rounded-lg border p-5">
        <div className="font-heading text-lg">Change password</div>
        <div className="text-xs text-[#8A8780] mb-3">Use a strong, unique password.</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Field label="Current password" type="password"
                   value={pwd.current_password}
                   onChange={v => setPwd({ ...pwd, current_password: v })}
                   testid="pw-current"/>
          <Field label="New password" type="password"
                   value={pwd.new_password}
                   onChange={v => setPwd({ ...pwd, new_password: v })}
                   testid="pw-new"/>
          <Field label="Confirm new" type="password"
                   value={pwd.confirm}
                   onChange={v => setPwd({ ...pwd, confirm: v })}
                   testid="pw-confirm"/>
        </div>
        <div className="mt-3 flex justify-end">
          <button onClick={changePw} disabled={saving} data-testid="pw-save"
                    className="px-4 py-2 rounded bg-[#0F172A] text-white text-sm disabled:opacity-50">
            {saving ? "Saving…" : "Update password"}
          </button>
        </div>
      </div>
    </div>
  );
}

function PadStatus({ refreshSummary }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ status: "on", off_reason: "", off_until: "" });
  const [busy, setBusy] = useState(false);
  const load = () => airqApi.get("/airq-auth/pad/status")
    .then(r => { setData(r.data);
      setForm({ status: r.data.status || "on",
                 off_reason: r.data.off_reason || "",
                 off_until:  (r.data.off_until || "").slice(0, 10) }); });
  useEffect(() => { load(); }, []);
  const save = async () => {
    if (form.status === "off" && !form.off_reason.trim())
      return toast.error("Reason is required when going off");
    setBusy(true);
    try {
      await airqApi.patch("/airq-auth/pad/status", {
        status: form.status,
        off_reason: form.status === "off" ? form.off_reason : null,
        off_until:  form.status === "off" ? (form.off_until || null) : null,
      });
      toast.success(`Pad marked ${form.status.toUpperCase()}`);
      await load();
      refreshSummary?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    setBusy(false);
  };
  if (!data) return <div className="text-sm text-[#8A8780]">Loading…</div>;
  const isOff = form.status === "off";
  return (
    <div className="space-y-4" data-testid="pad-status">
      <div className="bg-white rounded-lg border p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="font-heading text-lg">Pad operational status</div>
            <div className="text-xs text-[#8A8780]">
              Turn the pad off during maintenance, weather closures, or events.
            </div>
          </div>
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
            data.status === "off"
              ? "bg-[#FEE2E2] text-[#B91C1C]"
              : "bg-[#DCFCE7] text-[#166534]"}`}>
            {data.status === "off" ? "OFFLINE" : "OPEN"}
          </span>
        </div>
        <div className="flex gap-2 mb-3">
          <button onClick={() => setForm({ ...form, status: "on" })}
                    data-testid="status-on"
                    className={`px-4 py-2 rounded border text-sm ${
                      !isOff ? "bg-[#0F766E] text-white border-[#0F766E]"
                              : "bg-white text-[#5C5A55]"}`}>
            ● On (open)
          </button>
          <button onClick={() => setForm({ ...form, status: "off" })}
                    data-testid="status-off"
                    className={`px-4 py-2 rounded border text-sm ${
                      isOff ? "bg-[#B91C1C] text-white border-[#B91C1C]"
                            : "bg-white text-[#5C5A55]"}`}>
            ○ Off (closed)
          </button>
        </div>
        {isOff && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
            <Field label="Reason (e.g. maintenance, weather, event)"
                     value={form.off_reason}
                     onChange={v => setForm({ ...form, off_reason: v })}
                     testid="off-reason"/>
            <Field label="Off until (date)" type="date"
                     value={form.off_until}
                     onChange={v => setForm({ ...form, off_until: v })}
                     testid="off-until"/>
          </div>
        )}
        <div className="flex justify-end">
          <button onClick={save} disabled={busy} data-testid="status-save"
                    className="px-4 py-2 rounded bg-[#0F172A] text-white text-sm disabled:opacity-50">
            {busy ? "Saving…" : "Save status"}
          </button>
        </div>
      </div>
      <div className="bg-white rounded-lg border p-5">
        <div className="text-xs uppercase tracking-wider text-[#8A8780] mb-2">Recent changes</div>
        {(data.history || []).length === 0
          ? <div className="text-sm text-[#8A8780]">No status history yet</div>
          : <div className="space-y-1.5 max-h-64 overflow-y-auto">
              {data.history.slice().reverse().map((h, i) => (
                <div key={i} className="py-1.5 border-b last:border-0 text-sm flex items-center justify-between">
                  <div>
                    <span className={`px-2 py-0.5 rounded text-[10px] mr-2 ${
                      h.status === "off" ? "bg-[#FEE2E2] text-[#B91C1C]"
                                            : "bg-[#DCFCE7] text-[#166534]"}`}>
                      {h.status?.toUpperCase()}
                    </span>
                    {h.status === "off" && (
                      <span className="text-[#5C5A55]">
                        {h.off_reason || "(no reason)"}
                        {h.off_until ? ` · until ${h.off_until.slice(0,10)}` : ""}
                      </span>
                    )}
                  </div>
                  <span className="text-[10px] text-[#8A8780]">{(h.at || "").slice(0,16)}</span>
                </div>
              ))}
            </div>}
      </div>
    </div>
  );
}

function PadDispatches() {
  const [days, setDays] = useState(30);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");   // all|arrival|departure
  const load = () => {
    setLoading(true);
    airqApi.get(`/airq-auth/pad/dispatches?days=${days}`)
      .then(r => setRows(r.data.dispatches || []))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);
  const filtered = filter === "all" ? rows : rows.filter(r => r.direction === filter);
  return (
    <div className="space-y-3" data-testid="pad-dispatches">
      <div className="bg-white rounded-lg border p-4 flex flex-wrap items-center gap-2">
        <div className="font-heading text-lg mr-auto">Incoming dispatches</div>
        <select value={days} onChange={e => setDays(+e.target.value)}
                  data-testid="disp-days"
                  className="border rounded px-2 py-1 text-sm">
          {[7,14,30,60,90].map(d => <option key={d} value={d}>Last {d} days</option>)}
        </select>
        {["all","arrival","departure"].map(f => (
          <button key={f} onClick={() => setFilter(f)}
                    data-testid={`disp-filter-${f}`}
                    className={`px-3 py-1 rounded text-xs capitalize ${
                      filter === f ? "bg-[#0F766E] text-white"
                                    : "bg-[#F5F3EE] text-[#5C5A55]"}`}>{f}</button>
        ))}
        <button onClick={load} data-testid="disp-refresh"
                  className="p-1.5 rounded border hover:bg-[#F5F3EE]">
          <ArrowClockwise size={14}/>
        </button>
      </div>
      {loading ? <div className="text-sm text-[#8A8780]">Loading…</div>
        : filtered.length === 0
          ? <div className="bg-white rounded-lg border p-6 text-sm text-[#8A8780] text-center">
              No dispatches in the selected window.
            </div>
          : <div className="space-y-2">
              {filtered.map(d => <DispatchRow key={d.id} d={d}/>)}
            </div>}
    </div>
  );
}

function DispatchRow({ d }) {
  const arrivalCol = d.direction === "arrival" ? "#166534" : "#4338CA";
  return (
    <div className="bg-white rounded-lg border p-4" data-testid={`disp-row-${d.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="flex items-center gap-2">
            <span className="font-mono font-semibold">{d.mission_no}</span>
            <span className="text-[10px] uppercase px-2 py-0.5 rounded-full"
                    style={{ background: arrivalCol+"22", color: arrivalCol }}>
              {d.direction}
            </span>
            <span className="text-[10px] uppercase px-2 py-0.5 rounded-full bg-[#F5F3EE] text-[#5C5A55]">
              {d.payload_kind}
            </span>
            {d.priority && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#FEE2E2] text-[#B91C1C]">
              {d.priority}
            </span>}
          </div>
          <div className="text-[11px] text-[#8A8780] mt-1">
            {d.mission_kind} · {(d.aircraft?.kind || "aircraft")}
            {d.aircraft_call_sign ? ` · ${d.aircraft_call_sign}` : ""}
            {d.aircraft?.model ? ` · ${d.aircraft.model}` : ""}
          </div>
        </div>
        <div className="text-right">
          <div className={`text-[10px] uppercase px-2 py-0.5 rounded ${
            d.status === "completed" ? "bg-[#DCFCE7] text-[#166534]" :
            d.status === "dispatched" ? "bg-[#FEF3C7] text-[#B45309]" :
                                         "bg-[#E0E7FF] text-[#4338CA]"}`}>
            {d.status}
          </div>
          <div className="text-[10px] text-[#8A8780] mt-1">{(d.eta || "").slice(0, 16)}</div>
        </div>
      </div>
      {d.patient && (d.patient.name || d.patient.diagnosis || d.patient.receiving_hospital) && (
        <div className="mt-3 p-3 bg-[#FEF2F2] rounded border border-[#FECACA] text-xs">
          <div className="text-[10px] uppercase tracking-wider text-[#B91C1C] mb-1">Patient</div>
          <div><b>{d.patient.name || "Unknown"}</b>
            {d.patient.phone ? ` · ${d.patient.phone}` : ""}</div>
          {d.patient.diagnosis && <div className="text-[#5C5A55]">Dx: {d.patient.diagnosis}</div>}
          {d.patient.receiving_hospital && <div className="text-[#5C5A55]">
            Receiving: {d.patient.receiving_hospital}
          </div>}
        </div>
      )}
      {d.item && d.payload_kind === "cargo" && (
        <div className="mt-3 p-3 bg-[#F0F9FF] rounded border border-[#BAE6FD] text-xs">
          <div className="text-[10px] uppercase tracking-wider text-[#0369A1] mb-1">Cargo</div>
          <div><b>{d.item.kind}</b></div>
          {d.item.notes && <div className="text-[#5C5A55] mt-1">{d.item.notes}</div>}
        </div>
      )}
    </div>
  );
}

function PadStaff() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const load = () => {
    setLoading(true);
    airqApi.get("/airq-auth/pad/staff")
      .then(r => setRows(r.data.staff || []))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);
  const toggle = async (s) => {
    try {
      await airqApi.patch(`/airq-auth/pad/staff/${s.id}`, { is_active: !s.is_active });
      toast.success(`${s.name} ${!s.is_active ? "activated" : "deactivated"}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const remove = async (s) => {
    if (!window.confirm(`Remove ${s.name}?`)) return;
    try {
      await airqApi.delete(`/airq-auth/pad/staff/${s.id}`);
      toast.success("Removed");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="space-y-3" data-testid="pad-staff">
      <div className="bg-white rounded-lg border p-4 flex items-center justify-between">
        <div>
          <div className="font-heading text-lg">Managers & staff</div>
          <div className="text-xs text-[#8A8780]">Sign-in accounts under your pad.</div>
        </div>
        <button onClick={() => setShowAdd(true)} data-testid="staff-add"
                  className="flex items-center gap-1 px-3 py-2 rounded bg-[#0F766E] text-white text-sm">
          <Plus size={14}/> Add member
        </button>
      </div>
      {loading ? <div className="text-sm text-[#8A8780]">Loading…</div>
        : rows.length === 0
          ? <div className="bg-white rounded-lg border p-6 text-sm text-[#8A8780] text-center">
              No managers or staff yet. Add your first member.
            </div>
          : <div className="bg-white rounded-lg border">
              {rows.map(s => (
                <div key={s.id} className="p-3 border-b last:border-0 flex items-center gap-3"
                       data-testid={`staff-row-${s.id}`}>
                  <div className="w-9 h-9 rounded-full bg-[#0F766E]/10 flex items-center justify-center font-medium text-[#0F766E]">
                    {(s.name || "?").split(" ").map(x => x[0]).slice(0,2).join("").toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">
                      {s.name}
                      <span className={`ml-2 text-[10px] px-1.5 py-0.5 rounded ${
                        s.role === "manager"
                          ? "bg-[#E0E7FF] text-[#4338CA]"
                          : "bg-[#F5F3EE] text-[#5C5A55]"}`}>{s.role}</span>
                      {!s.is_active && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-[#FEE2E2] text-[#B91C1C]">inactive</span>}
                    </div>
                    <div className="text-[11px] text-[#8A8780] truncate">
                      {s.email}{s.phone ? ` · ${s.phone}` : ""}
                    </div>
                  </div>
                  <button onClick={() => toggle(s)} data-testid={`staff-toggle-${s.id}`}
                            className="text-xs text-[#5C5A55] hover:text-[#0F766E]">
                    {s.is_active ? "Deactivate" : "Activate"}
                  </button>
                  <button onClick={() => remove(s)} data-testid={`staff-remove-${s.id}`}
                            className="p-1.5 text-[#B91C1C] hover:bg-[#FEE2E2] rounded">
                    <Trash size={14}/>
                  </button>
                </div>
              ))}
            </div>}
      {showAdd && <AddStaffModal onClose={() => setShowAdd(false)} onSaved={load}/>}
    </div>
  );
}

function AddStaffModal({ onClose, onSaved }) {
  const [f, setF] = useState({ name: "", email: "", phone: "",
                                    password: "", role: "staff" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!f.name || !f.email || !f.password)
      return toast.error("Name, email, password required");
    if (f.password.length < 6) return toast.error("Password must be 6+ chars");
    setBusy(true);
    try {
      await airqApi.post("/airq-auth/pad/staff", f);
      toast.success("Member added — share credentials securely");
      onSaved?.();
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
           onClick={onClose}>
      <div className="bg-white rounded-lg w-full max-w-md p-5"
             onClick={e => e.stopPropagation()} data-testid="staff-add-modal">
        <div className="font-heading text-lg mb-1">Add manager / staff</div>
        <div className="text-xs text-[#8A8780] mb-4">They sign in with these credentials at /airq/login.</div>
        <div className="space-y-3">
          <Field label="Full name" value={f.name}
                   onChange={v => setF({ ...f, name: v })} testid="add-name"/>
          <Field label="Email" type="email" value={f.email}
                   onChange={v => setF({ ...f, email: v })} testid="add-email"/>
          <Field label="Phone" value={f.phone}
                   onChange={v => setF({ ...f, phone: v })} testid="add-phone"/>
          <Field label="Password (min 6)" type="password" value={f.password}
                   onChange={v => setF({ ...f, password: v })} testid="add-password"/>
          <div>
            <label className="text-[11px] uppercase text-[#8A8780]">Role</label>
            <select value={f.role} onChange={e => setF({ ...f, role: e.target.value })}
                      data-testid="add-role"
                      className="w-full border rounded px-3 py-2 text-sm mt-1">
              <option value="staff">Staff (day-to-day operations)</option>
              <option value="manager">Manager (full delegate)</option>
            </select>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="px-3 py-2 rounded border text-sm text-[#5C5A55]">
            Cancel
          </button>
          <button onClick={save} disabled={busy} data-testid="add-save"
                    className="px-4 py-2 rounded bg-[#0F766E] text-white text-sm disabled:opacity-50">
            {busy ? "Saving…" : "Create account"}
          </button>
        </div>
      </div>
    </div>
  );
}

function PadRevenue() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  useEffect(() => {
    airqApi.get(`/airq-auth/pad/revenue?days=${days}`).then(r => setData(r.data));
  }, [days]);
  if (!data) return <div className="text-sm text-[#8A8780]">Loading…</div>;
  const money = (n) => `₹${(n || 0).toLocaleString("en-IN")}`;
  return (
    <div className="space-y-3" data-testid="pad-revenue">
      <div className="bg-white rounded-lg border p-4 flex items-center justify-between">
        <div>
          <div className="font-heading text-lg">Revenue</div>
          <div className="text-xs text-[#8A8780]">
            {money(data.landing_fee_inr)} per completed landing at your pad.
          </div>
        </div>
        <select value={days} onChange={e => setDays(+e.target.value)}
                  data-testid="rev-days"
                  className="border rounded px-2 py-1 text-sm">
          {[7,14,30,60,90].map(d => <option key={d} value={d}>Last {d} days</option>)}
        </select>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Completed landings" v={data.completed_landings} c="#166534"/>
        <Stat label="In-flight landings" v={data.inflight_landings}  c="#B45309"/>
        <Stat label="Revenue earned"     v={money(data.revenue_inr)} c="#0F766E"/>
        <Stat label="Projected revenue"  v={money(data.projected_inr)} c="#4338CA"/>
      </div>
      <div className="bg-white rounded-lg border p-4">
        <div className="text-xs uppercase tracking-wider text-[#8A8780] mb-2">By mission kind</div>
        {Object.keys(data.by_kind || {}).length === 0
          ? <div className="text-sm text-[#8A8780]">No landings in window</div>
          : <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {Object.entries(data.by_kind).map(([k, v]) => (
                <div key={k} className="p-3 rounded bg-[#F5F3EE]">
                  <div className="text-lg font-heading">{v}</div>
                  <div className="text-[11px] text-[#8A8780]">{k}</div>
                </div>
              ))}
            </div>}
      </div>
      <div className="bg-white rounded-lg border p-4">
        <div className="text-xs uppercase tracking-wider text-[#8A8780] mb-2">
          Landings log · {(data.landings || []).length}
        </div>
        {(data.landings || []).length === 0
          ? <div className="text-sm text-[#8A8780]">No landings recorded</div>
          : <div className="space-y-1 max-h-64 overflow-y-auto">
              {data.landings.map((l, i) => (
                <div key={i} className="flex items-center justify-between py-1.5 border-b last:border-0 text-sm">
                  <div>
                    <div className="font-mono">{l.mission_no}</div>
                    <div className="text-[10px] text-[#8A8780]">
                      {l.mission_kind} · {(l.created_at || "").slice(0,10)}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold">{money(l.amount_inr)}</div>
                    <div className="text-[10px] text-[#8A8780]">{l.status}</div>
                  </div>
                </div>
              ))}
            </div>}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-wider text-[#8A8780]">{label}</label>
      <input type={type} value={value || ""} onChange={e => onChange(e.target.value)}
               data-testid={testid}
               className="w-full border rounded px-3 py-2 mt-1 text-sm"/>
    </div>
  );
}
function AeromedAdmin({ s }) {
  return (
    <div className="space-y-4" data-testid="aeromed-admin-panel">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Aircraft" v={s.aircraft_count} c="#0F766E"/>
        <Stat label="Missions" v={s.missions_count} c="#4338CA"/>
        <Stat label="In flight" v={s.aircraft.filter(a => a.status==="in_flight").length} c="#B91C1C"/>
        <Stat label="Available" v={s.aircraft.filter(a => a.status==="available").length} c="#166534"/>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="bg-white rounded-lg border p-4">
          <div className="text-xs uppercase tracking-wider text-[#8A8780] mb-2">Aircraft</div>
          {s.aircraft.length === 0 ? <div className="text-sm text-[#8A8780]">No aircraft</div>
            : s.aircraft.map(a => (
              <div key={a.id} className="py-1.5 border-b last:border-0 text-sm flex justify-between">
                <div>
                  <div className="font-mono font-medium">{a.call_sign}</div>
                  <div className="text-[11px] text-[#8A8780]">{a.kind} · {a.ambulance_type || "n/a"}</div>
                </div>
                <span className="text-[10px] uppercase tracking-wider self-center"
                        style={{ color: a.status==="available" ? "#166534"
                                          : a.status==="in_flight" ? "#B45309"
                                          : "#7F1D1D" }}>
                  {a.status}
                </span>
              </div>
            ))}
        </div>
        <div className="bg-white rounded-lg border p-4">
          <div className="text-xs uppercase tracking-wider text-[#8A8780] mb-2">Recent missions</div>
          {s.missions.slice(0, 20).map(m => (
            <div key={m.id} className="py-1.5 border-b last:border-0 text-sm">
              <div className="font-medium">{m.mission_no}</div>
              <div className="text-[11px] text-[#8A8780]">
                {m.mission_kind} · P{m.priority?.replace("P","")} · {m.status}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function RegulatorPanel({ s }) {
  const scope = s.all_states ? "All states (national)" :
                    (s.state_names || []).join(" · ") || "no states";
  const label = s.subtype === "dgca" ? "DGCA" : "State Aviation";
  const [feed, setFeed] = useState([]);
  useEffect(() => {
    airqApi.get("/airq-auth/activity?limit=15")
      .then(r => setFeed(r.data.feed || [])).catch(() => {});
  }, []);
  const downloadAudit = () => {
    const tok = localStorage.getItem(TOKEN_KEY);
    // Build a signed URL with token in Authorization is hard for downloads;
    // simplest: fetch as blob and save.
    fetch(`${API}/airq-auth/audit-export.pdf?days=30`,
            { headers: { Authorization: `Bearer ${tok}` } })
      .then(async r => {
        if (!r.ok) throw new Error(await r.text());
        const blob = await r.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `airq-audit-${s.subtype}-${new Date().toISOString().slice(0,10)}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
        toast.success("Audit PDF downloaded");
      })
      .catch(e => toast.error(String(e).slice(0, 120)));
  };
  return (
    <div className="space-y-4" data-testid="regulator-panel">
      <div className="bg-white rounded-lg border p-5 flex items-start justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-wider text-[#8A8780]">{label}</div>
          <div className="font-heading text-2xl mt-1">Regulator scope</div>
          <div className="text-sm text-[#5C5A55] mt-1">{scope}</div>
        </div>
        <button onClick={downloadAudit} data-testid="reg-audit-pdf"
                  className="whitespace-nowrap px-3 py-2 rounded-lg bg-[#0F172A] text-[#67E8F9] text-xs font-medium hover:bg-black transition">
          Download 30-day audit ↓
        </button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Pads in scope" v={s.pads_count} c="#0369A1"/>
        <Stat label="Clearances" v={(s.clearances || []).length} c="#0F766E"/>
        <Stat label="Open incidents" v={(s.incidents || []).length} c="#B91C1C"/>
        <Stat label="Pending approvals" v={(s.pending_approvals || []).length} c="#B45309"/>
      </div>
      {/* Peer activity feed */}
      <div className="bg-white rounded-lg border p-4" data-testid="reg-activity">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs uppercase tracking-wider text-[#8A8780]">Peer activity · who cleared what</div>
          <span className="text-[10px] text-[#8A8780]">{feed.length} recent</span>
        </div>
        {feed.length === 0
          ? <div className="text-sm text-[#8A8780]">No recent regulator activity</div>
          : <div className="space-y-1.5 max-h-56 overflow-y-auto">
              {feed.map((f, i) => (
                <div key={i} className="flex items-center gap-2 text-xs py-1 border-b last:border-0">
                  <div className="w-7 h-7 rounded-full bg-[#0F766E]/10 flex items-center justify-center font-medium text-[#0F766E]">
                    {f.actor_initials}
                  </div>
                  <div className="flex-1">
                    <div><b>{f.action.replace(/^(gov\.|airq\.)/,"").replace(/\./g," ")}</b>
                          {" · "}<span className="text-[#8A8780]">{f.subject_id?.slice(0,10)}</span></div>
                    <div className="text-[10px] text-[#8A8780]">{(f.at||"").slice(0,16)}</div>
                  </div>
                </div>
              ))}
            </div>}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="bg-white rounded-lg border p-4">
          <div className="text-xs uppercase tracking-wider text-[#8A8780] mb-2">
            Recent clearance queue
          </div>
          {(s.clearances || []).length === 0 ? <div className="text-sm text-[#8A8780]">No missions in scope</div>
            : s.clearances.slice(0,20).map(m => (
              <div key={m.id} className="flex items-center justify-between py-1.5 border-b last:border-0 text-sm">
                <div>
                  <div className="font-medium">{m.mission_no}</div>
                  <div className="text-[11px] text-[#8A8780]">
                    {m.mission_kind} · {m.aircraft_call_sign || "unassigned"}
                  </div>
                </div>
                <span className="text-[10px] uppercase tracking-wider"
                        style={{ color: m.clearance_status === "cleared" ? "#166534"
                                           : m.clearance_status === "denied" ? "#7F1D1D"
                                           : "#4338CA" }}>
                  {m.clearance_status || "pending"}
                </span>
              </div>
            ))}
        </div>
        <div className="bg-white rounded-lg border p-4">
          <div className="text-xs uppercase tracking-wider text-[#8A8780] mb-2">
            Open incidents
          </div>
          {(s.incidents || []).length === 0 ? <div className="text-sm text-[#8A8780]">All clear</div>
            : s.incidents.map(i => (
              <div key={i.id} className="py-1.5 border-b last:border-0 text-sm">
                <div className="font-medium">{i.incident_no} · {i.severity}</div>
                <div className="text-[11px] text-[#8A8780]">{i.summary}</div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}

function Kv({ k, v }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-[#8A8780]">{k}</div>
      <div className="text-sm font-medium">{v || "—"}</div>
    </div>
  );
}
function Stat({ label, v, c }) {
  return (
    <div className="p-4 bg-white rounded-lg border">
      <div className="text-[22px] font-heading font-semibold" style={{ color: c }}>{v}</div>
      <div className="text-[11px] uppercase tracking-[0.15em] text-[#8A8780] mt-1">{label}</div>
    </div>
  );
}
